import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from './api.js';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const VISITOR_KEY = 'ow.visitor';

/**
 * A random token kept in this browser so a day's visits can be counted as one
 * visitor. It is not derived from anything about the person, and the server
 * hashes it with a salt that changes daily, so it cannot follow anyone from
 * one day to the next.
 */
function visitorToken(): string | null {
  try {
    let token = localStorage.getItem(VISITOR_KEY);
    if (!token) {
      token = crypto.randomUUID().replace(/-/g, '');
      localStorage.setItem(VISITOR_KEY, token);
    }
    return token;
  } catch {
    return null; // private mode, or storage refused, count nothing rather than guess
  }
}

/** Send one GA4 event; a no-op when the tag is blocked or not loaded. */
export function track(event: string, params: Record<string, unknown> = {}): void {
  window.gtag?.('event', event, params);
}

export type AuthMethod = 'email' | 'google' | 'apple' | 'parish';

/**
 * GA4's recommended sign_up / login events. A Google or Apple sign-in that
 * created the account counts as a sign-up (the API says so in is_new_user).
 */
export function trackAuth(method: AuthMethod, isNewUser: boolean): void {
  track(isNewUser ? 'sign_up' : 'login', { method });
}

/**
 * GA4's recommended search event, for the site's own search boxes. Called
 * once a term has settled (the boxes filter as you type), so a word typed
 * letter by letter is one search, not seven.
 */
export function trackSearch(term: string, placement: string): void {
  const search_term = term.trim().toLowerCase();
  if (search_term.length < 3) return;
  track('search', { search_term, placement });
}

/** Report a filter-as-you-type search box's term once it has rested. */
export function useSearchTracking(term: string): void {
  useEffect(() => {
    const t = term.trim();
    if (t.length < 3) return;
    const timer = window.setTimeout(() => trackSearch(t, window.location.pathname), 1500);
    return () => window.clearTimeout(timer);
  }, [term]);
}

/**
 * Clicks that leave the site for an app store or PayPal, wherever the link
 * sits: one listener on the document instead of a handler on every link.
 * Recorded as app_store_click { store } and donate_click { method }, both
 * with the page they were made from.
 */
export function useOutboundTracking(): void {
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const anchor = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      let host = '';
      try {
        host = new URL(anchor.href).hostname;
      } catch {
        return;
      }
      const placement = window.location.pathname;
      if (host === 'apps.apple.com') track('app_store_click', { store: 'ios', placement });
      else if (host === 'play.google.com') track('app_store_click', { store: 'android', placement });
      else if (host.endsWith('paypal.com')) track('donate_click', { method: 'paypal_link', placement });
    }
    document.addEventListener('click', onClick, { capture: true });
    return () => document.removeEventListener('click', onClick, { capture: true });
  }, []);
}

/**
 * Page-view tracking for SPA navigation: to GA4 for the long view, and to our
 * own API so the super-admin dashboard can show traffic as it happens.
 *
 * The gtag snippet in index.html is configured with `send_page_view: false`, so
 * this hook is the single source of page_view events, including the initial
 * load. Mounted after RouteSeo in the tree so document.title is already set for
 * the route.
 */
export function usePageViews(): void {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.gtag?.('event', 'page_view', {
      page_path: pathname + search,
      page_location: window.location.href,
      page_title: document.title,
    });

    const visitor = visitorToken();
    if (!visitor) return;
    // Best effort: a page view that fails to record is not worth a broken page.
    void apiFetch('/analytics/visit', {
      method: 'POST',
      body: { path: pathname, visitor },
      skipAuth: true,
    }).catch(() => {});
  }, [pathname, search]);
}
